import {css} from 'aphrodite';
import React from 'react';

import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import StepDocumentLink from 'notebook/worksheets/steps/StepDocumentLink';
import {UpdateTaskParams} from 'processes/process-task-details/utils/updateTaskDetailsTask';
import {NO_ACCESS_TEXT} from 'processes/ProcessConstants';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';

import AssigneeField from '../fields/AssigneeField';
import FormattedTextField from '../fields/FormattedTextField';
import PlaceholderFieldValue from '../fields/PlaceholderFieldValue';
import {ScheduledOnField} from '../fields/ScheduledOnField';
import StatusField from '../fields/StatusField';
import {
  ProcessTaskDetailsRowContent,
  PROCESS_TASK_DETAILS_PAGE_STYLES,
  getProcessTaskDetailsLabels,
  ProcessTaskGql,
} from '../ProcessTaskDetailsPageConstants';

export function ProcedureMethodTaskField(props: {processTask: ProcessTaskGql}): JSX.Element {
  if (
    props.processTask.procedureStepExecutionInstance == null ||
    isInaccessible(props.processTask.procedureStepExecutionInstance)
  ) {
    return <PlaceholderFieldValue />;
  } else if (
    isInaccessible(props.processTask.procedureStepExecutionInstance.methodExecutionInstance) ||
    props.processTask.procedureStepExecutionInstance.methodExecutionInstance.methodTask == null
  ) {
    const templateMethodVersion =
      props.processTask.procedureStepExecutionInstance.methodExecutionInstance.templateMethodVersion;

    return (
      <FormattedTextField
        text={isInaccessible(templateMethodVersion) ? NO_ACCESS_TEXT : templateMethodVersion.name}
      />
    );
  } else {
    const methodTask = props.processTask.procedureStepExecutionInstance.methodExecutionInstance.methodTask;
    const taskId = getAccessibleOrInaccessibleId(methodTask);
    return (
      <BenchChip
        id={taskId}
        className={css(PROCESS_TASK_DETAILS_PAGE_STYLES.chipHeight)}
        size={ChipSize.TABLE_CELL}
      />
    );
  }
}

export function getProcedureStepTaskDetailsSummaryRows(
  processTask: ProcessTaskGql,
  onUpdateTask: (updatedTask: UpdateTaskParams) => Promise<void>,
  {
    experimentRunContent,
    unitOperationContent,
    procedureTemplateContent,
    conditionsContent,
    replicatesContent,
  }: {
    experimentRunContent: ProcessTaskDetailsRowContent;
    unitOperationContent: ProcessTaskDetailsRowContent;
    procedureTemplateContent: ProcessTaskDetailsRowContent;
    conditionsContent: ProcessTaskDetailsRowContent;
    replicatesContent: ProcessTaskDetailsRowContent;
  }
): Array<ProcessTaskDetailsRowContent> {
  let document = null;
  if (
    processTask.procedureStepExecutionInstance != null &&
    !isInaccessible(processTask.procedureStepExecutionInstance)
  ) {
    document = processTask.procedureStepExecutionInstance.document;
  }
  const processTaskDetailsLabels = getProcessTaskDetailsLabels();
  return [
    {
      label: processTaskDetailsLabels.STATUS,
      component: <StatusField processTask={processTask} onUpdateTask={onUpdateTask} />,
    },
    {
      label: processTaskDetailsLabels.ASSIGNEE,
      component: <AssigneeField processTask={processTask} onUpdateTask={onUpdateTask} />,
    },
    {
      label: processTaskDetailsLabels.SCHEDULED_ON,
      component: <ScheduledOnField processTask={processTask} onUpdateTask={onUpdateTask} />,
    },
    {
      label: processTaskDetailsLabels.EXECUTED_IN,
      component:
        document != null && !isInaccessible(document) ? (
          <StepDocumentLink
            href={document.editUrl}
            className={css(PROCESS_TASK_DETAILS_PAGE_STYLES.rowHeight)}
          >
            {document.name}
          </StepDocumentLink>
        ) : (
          <PlaceholderFieldValue />
        ),
    },
    experimentRunContent,
    unitOperationContent,
    procedureTemplateContent,
    conditionsContent,
    replicatesContent,
  ];
}
